import { InputHTMLAttributes, useEffect, useState } from 'react';
import { Controller, useForm, UseFormClearErrors } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { collectFormErrors } from '../../../../utils/collect-form-errors';
import { Button } from '../../../atoms/button/button';
import { FormError } from '../../../atoms/form-error/form-error';
import { Header } from '../../../atoms/header/header';
import { Input } from '../../../atoms/input/input';
import { PhoneInput } from '../../../atoms/phone-input/phone-input';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';

const InlineLinkButton = ({
  children,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
}) => (
  <button className="text-blue-600 underline cursor-pointer" onClick={onClick} type="button">
    {children}
  </button>
);

const AlreadyHaveCodeHelpText = ({
  navigateToCode,
  t,
}: {
  readonly navigateToCode: () => void;
  readonly t: (key: string) => string;
}) => {
  return (
    <Text>
      <Trans
        components={{
          enterCodeLink: (
            <InlineLinkButton onClick={navigateToCode}>{t('page.mfaSetup.phone.enterCodeHere')}</InlineLinkButton>
          ),
        }}
        i18nKey="page.mfaSetup.phone.alreadyHaveCode"
      />
    </Text>
  );
};

const HaventReceivedCodeHelpText = ({
  onChangeNumber,
  onStartAgain,
  onResend,
  t,
}: {
  readonly onChangeNumber?: () => void;
  readonly onStartAgain?: () => void;
  readonly onResend: () => void | Promise<void>;
  readonly t: (key: string) => string;
}) => {
  const resendLink = <InlineLinkButton onClick={onResend}>{t('page.mfaSetup.code.resend')}</InlineLinkButton>;

  const secondLink = onChangeNumber ? (
    <InlineLinkButton onClick={onChangeNumber}>{t('page.mfaSetup.code.changeNumber')}</InlineLinkButton>
  ) : (
    onStartAgain && <InlineLinkButton onClick={onStartAgain}>{t('page.mfaSetup.code.startAgain')}</InlineLinkButton>
  );

  if (!secondLink) return null;

  return (
    <Text>
      <Trans
        components={{
          resendLink,
          secondLink,
        }}
        i18nKey={'page.mfaSetup.code.helpPrefix'}
      />
    </Text>
  );
};

const MfaHelpText = ({
  onChangeNumber,
  onResend,
  onStartAgain,
  navigateToCode,
  t,
}: {
  readonly onChangeNumber?: () => void;
  readonly onStartAgain?: () => void;
  readonly onResend?: () => void | Promise<void>;
  readonly navigateToCode?: () => void;
  readonly t: (key: string) => string;
}) => {
  if (onResend && (onChangeNumber || onStartAgain))
    return (
      <HaventReceivedCodeHelpText
        onChangeNumber={onChangeNumber}
        onResend={onResend}
        onStartAgain={onStartAgain}
        t={t}
      />
    );

  if (navigateToCode) return <AlreadyHaveCodeHelpText navigateToCode={navigateToCode} t={t} />;

  return null;
};

type RegisterType = ReturnType<typeof useForm<{ value: string }>>['register'];
type ControlType = ReturnType<typeof useForm<{ value: string }>>['control'];
type SetErrorType = ReturnType<typeof useForm<{ value: string }>>['setError'];
type ClearErrorType = UseFormClearErrors<{ value: string }>;

type MfaInputProps = {
  readonly autoComplete?: string;
  readonly clearErrors: ClearErrorType;
  readonly control: ControlType;
  readonly errorMessages: string[];
  readonly errorRequired: string;
  readonly errorInvalid: string;
  readonly inputId: string;
  readonly inputLabel?: string;
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  readonly placeholder?: string;
  readonly register: RegisterType;
  readonly setError: SetErrorType;
};

const usePhoneInputRules = (errorRequired: string, errorInvalid: string, isValid: boolean) => {
  return {
    required: errorRequired,
    validate: (value: string) => {
      if (value?.trim().length && !isValid) return errorInvalid;
      return true;
    },
  };
};

const MfaInput = ({
  clearErrors,
  control,
  errorMessages,
  errorInvalid,
  errorRequired,
  inputId,
  inputLabel,
  register,
  setError,
  ...inputProps
}: MfaInputProps) => {
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [isPhoneCountryValid, setIsPhoneCountryValid] = useState(true);

  const isPhoneInput = inputId === 'phoneNumber';

  useEffect(() => {
    isPhoneInput && !isPhoneCountryValid ? setError('value', { message: errorInvalid }) : clearErrors('value');
  }, [clearErrors, setError, isPhoneCountryValid, isPhoneInput, errorInvalid]);

  const phoneRules = usePhoneInputRules(errorRequired, errorInvalid, isPhoneNumberValid && isPhoneCountryValid);

  return (
    <div className="flex flex-col gap-2">
      {inputLabel ? <Text>{inputLabel}</Text> : null}
      {isPhoneInput ? (
        <Controller
          control={control}
          name="value"
          render={({ field: { name, onBlur, onChange, value } }) => (
            <PhoneInput
              id={inputId}
              name={name}
              onBlur={onBlur}
              onChangeNumber={onChange}
              setIsValidCountry={setIsPhoneCountryValid}
              setIsValidNumber={setIsPhoneNumberValid}
              value={value}
              variant={errorMessages.length > 0 ? 'error' : 'default'}
            />
          )}
          rules={phoneRules}
          {...inputProps}
        />
      ) : (
        <Input
          {...register('value', {
            required: errorRequired,
          })}
          id={inputId}
          variant={errorMessages.length > 0 ? 'error' : 'default'}
          {...inputProps}
        />
      )}
    </div>
  );
};

const MfaHeader = ({ title, description }: { readonly title?: string; readonly description?: string }) => {
  return (
    <>
      {title ? <Header level="h1">{title}</Header> : null}
      {description ? <Text>{description}</Text> : null}
    </>
  );
};

type MfaFieldFormProps = {
  readonly autoComplete?: string;
  readonly buttonLabel: string;
  readonly description?: string;
  readonly inputLabel?: string;
  readonly errorInvalid?: string;
  readonly errorRequired: string;
  readonly inputId: string;
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  readonly onChangeNumber?: () => void;
  readonly onStartAgain?: () => void;
  readonly onResend?: () => void | Promise<void>;
  readonly onSubmit: (value: string) => Promise<void> | void;
  readonly placeholder?: string;
  readonly title?: string;
  readonly navigateToCode?: () => void;
};

const MfaFieldForm = ({
  buttonLabel,
  description,
  errorInvalid,
  errorRequired,
  navigateToCode,
  onChangeNumber,
  onStartAgain,
  onResend,
  onSubmit,
  title,
  ...inputProps
}: MfaFieldFormProps) => {
  const { t } = useTranslation();
  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ value: string }>({ defaultValues: { value: '' } });

  const submitHandler = handleSubmit(
    createSubmitHandler({
      onSubmit: async ({ value }) => {
        await onSubmit(value.trim());
      },
      setError,
    })
  );

  const errorMessages = collectFormErrors(errors, t, ['value']);

  return (
    <form onSubmit={submitHandler}>
      <div className="flex flex-col gap-6">
        <MfaHeader description={description} title={title} />
        <MfaInput
          clearErrors={clearErrors}
          control={control}
          errorInvalid={errorInvalid ?? t('auth.common.errors.phoneNumberInvalid')}
          errorMessages={errorMessages}
          errorRequired={errorRequired}
          register={register}
          setError={setError}
          {...inputProps}
        />
        <FormError error={errorMessages.length > 0 ? errorMessages : null} icon="alert-circle" />
        <MfaHelpText
          navigateToCode={navigateToCode}
          onChangeNumber={onChangeNumber}
          onResend={onResend}
          onStartAgain={onStartAgain}
          t={t}
        />
        <Button className="w-full" loading={isSubmitting} type="submit">
          {buttonLabel}
        </Button>
      </div>
    </form>
  );
};

export { HaventReceivedCodeHelpText, MfaFieldForm };
